import * as React from 'react';
import DataGrid from 'devextreme-react/data-grid';
import { withStyles } from '@material-ui/core/styles';
import { ILotDialogProps } from './LotDialog.properties';
import styles from './LotDialog.styles';
import FormViewModal from 'components/common/Modals/FormViewModal';
import { IActionItem } from 'components/common/Modals/FormViewModal.properties';
import NumericField from '@markinson/uicomponents-v2/NumericField';
// import ProductLotLookup from 'components/common/ProductLotLookup';
import { default as MUIButton } from '@material-ui/core/Button';
import CheckCircleIcon from '@markinson/uicomponents-v2/SvgIcons/CheckCircle';
import { Cancel as CancelIcon } from '@markinson/uicomponents-v2/SvgIcons';
import { Table, TableBody, TableRow, TableCell } from '@material-ui/core';
import { IBinLotFacade } from 'api/swaggerTypes';
import classNames from 'classnames';

import { isNull } from 'utils/utils';
import ITextFieldHandle from '@markinson/uicomponents-v2/TextField/TextField.handle';
import { ENTER_KEY } from 'utils/constants';
import AddLots from './AddLots';
import DynamicDataGrid from '../DataGridDevEx/DynamicDataGrid';
import { createDataSource } from '../DataGridDevEx/DataGrid.hooks';
import { useCalculateLots } from 'api/putawayLots/lots';
import BasicLookupActionField from 'components/FormView/Fields/BasicLookupActionField/BasicLookupActionField';
import { generateColumns, LOT_FOCUS_TIMEOUT } from './constants';

const LotDialog = (props: ILotDialogProps): JSX.Element => {
  const {
    classes, title, open, onApply, onCancel, RequiredQuantity, BinLots,
    WarehouseEntity, ProductId, isBinTracked = false, defaultBin, DocumentType
  } = props;
  const [enteredQuantity, setEnteredQuantity] = React.useState(0);
  const [quantity, setQuantity] = React.useState(0);
  const [lines, setLines] = React.useState<IBinLotFacade[]>([]);
  const [productLot, setProductLot] = React.useState<any>();
  const [productLotValue, setProductLotValue] = React.useState<string>();
  const [isApplyDisabled, setIsApplyDisabled] = React.useState<boolean>(true);
  const [initialSetQuantity, setInitialSetQuantity] = React.useState<boolean>(true);
  const [isEntryDataCleared, setIsEntryDataCleared] = React.useState<boolean>(true);
  const { mutateAsync: calculateLotsMutateAsync } = useCalculateLots();

  const [addLotsOpen, setAddLotsOpen] = React.useState(false);

  const gridRef = React.useRef<DataGrid>();

  const quantityRef = React.useRef<ITextFieldHandle>();
  const lotRef = React.useRef<any>(null);

  const [editData, setEditData] = React.useState(null);

  React.useEffect(
    () => { setLines(BinLots); },
    [BinLots]);

  React.useEffect(
    () => { updateQuantities(); },
    [lines]);

  React.useEffect(
    () => {
      shouldDisableApply(quantity);
    },
    [productLot, quantity]);

  React.useLayoutEffect(
    () => {
      if (
        isEntryDataCleared &&
        !productLotValue &&
        !productLot &&
        quantity === 0 &&
        lotRef.current
      ) {
        setIsEntryDataCleared(false);
        setTimeout(
          () => {
            lotRef.current?.focus();
          },
          LOT_FOCUS_TIMEOUT
        );
      }
    },
    [isEntryDataCleared, productLotValue, productLot, quantity]
  );

  const confirmationActions = (): IActionItem[] => {
    return [
      {
        title: 'Ok',
        iconName: 'CheckCircle',
        disabled: !RequiredQuantity || Number(enteredQuantity) !== Number(RequiredQuantity),
        listener: async () => {
          if (onApply) {
            await onApply(lines);
          }
        }
      },
      {
        iconName: 'Cancel',
        isDefault: true,
        title: 'Cancel',
        listener: async () => {
          if (onCancel) {
            await onCancel();
          }
        }
      },
      {
        title: 'New',
        iconName: 'CheckCircle',
        style: {
          position: 'absolute',
          left: 18,
          float: 'unset',
        },
        listener: async () => {
          setAddLotsOpen(true);
        }
      }];
  };

  const NumberOfDecimalPlaces = 2;

  const shouldDisableApply = (value: number) => {
    setIsApplyDisabled(isNull(productLot) || (isNull(value) || value === 0));
  };

  const addLine = async (qty?: number) => {
    if (isApplyDisabled) {
      return;
    }
    const quantityToAdd = isNull(qty) ? quantity : qty;
    const newLine = {
      ...(productLot ?? {}),
      Quantity: quantityToAdd,
      FastLine: true
    };

    await calculateLotsHandler(newLine);
    clearEntryData();
  };

  const undoChanges = () => {
    clearEntryData();
  };

  const clearEntryData = () => {
    setQuantity(0);
    setProductLot(undefined);
    setProductLotValue(undefined);
    setIsEntryDataCleared(true);
  };

  const getSum = (type) => {
    return isNull(lines) ? 0 : lines.reduce((a, b) => (a + +b[type]), 0);
  };

  const updateQuantities = () => {
    setEnteredQuantity(getSum('Quantity') || 0);
  };

  const onQuantityFocus = () => {
    if (quantityRef.current) {
      quantityRef.current.select();
    }
  };

  const quantityBlur = () => {
    if (quantityRef.current) {
      quantityRef.current.blur();
    }
  };

  const onQuantityKeyDown = (event) => {
    if (event.keyCode === ENTER_KEY) {
      quantityBlur();
      addLine(Number(event.target.value));
    }
  };
  const onQuantityChange = (value) => {
    shouldDisableApply(value);
  };

  const fetchLines = React.useCallback(
    async () => {
      return lines;
    },
    [lines]
  );

  async function calculateLineRow(data: any, noFastLine: false): Promise<any> {
    const selectedIndex = lines?.findIndex((item) => item?.LotSerial === editData?.LotSerial);
    const selectedLot: any = lines?.[selectedIndex];

    let LotSerialChanged = false;
    if (editData?.LotSerial !== data?.LotSerial) {
      LotSerialChanged = true;
    }

    if (noFastLine) {
      delete selectedLot.FastLine;
    }

    const binLotCriteria = { ...(!isBinTracked && { Bin: '' }), ...{ ...selectedLot, ...data, LotSerialChanged, LotSerialNumber: selectedIndex + 1 } };
    if (binLotCriteria?.Bin == null) {
      binLotCriteria.Bin = '';
    }

    const response = await calculateLotsMutateAsync({ ProductId, Warehouse: WarehouseEntity, BinLotCriteria: { ...binLotCriteria, DocumentType: DocumentType || undefined }, BinLots: lines ?? [] });
    setLines(response?.LotSerial ?? []);

    if (response?.Status) {
      refreshDataGrid();
      setAddLotsOpen(false);
      setEditData(null);
    }
  }

  const refreshDataGrid = React.useCallback(
    async () => {
      gridRef.current?.instance?.refresh();
      gridRef.current?.instance?.cancelEditData();
    },
    []
  );

  const linesDataSource = React.useMemo(
    () => createDataSource(
      'LotSerial',
      { fetch: fetchLines },
    ),
    [lines]
  );

  async function deleteClick(_: string, row: any): Promise<void> {
    const filteredLines = lines?.filter((line) => line?.LotSerial !== row?.LotSerial);
    setLines(filteredLines ?? []);
    await gridRef.current?.instance?.refresh(true);
  }

  function onEditClick(_: string | number, rowData: any): void {
    setEditData(rowData);
    setAddLotsOpen(true);
  }

  const getModalContent = () => {
    return <div className={classes.mainDiv}>

      <div className={classes.lotLineDiv}>
        <div className={classes.inlineFields}>
          <BasicLookupActionField
            ref={lotRef}
            placeholder={'Lot'}
            label={'Lot'}
            size={'large'}
            searchScreenTitle={'Product Lots'}
            lookupName={'WarehouseProductBinLot'}
            value={productLotValue}
            onSelectedItemChange={(value, searchText) => {
              if (value && (initialSetQuantity || quantity === 0)) {
                setTimeout(
                  () => {
                    quantityRef?.current?.focus();
                  },
                  0
                );
                if (quantity < 1) {
                  setQuantity(1);
                }
                setInitialSetQuantity(false);
              }

              if (value?.Code !== productLotValue) {
                if (value) {
                  setProductLotValue(value?.Code);
                  setProductLot(value?.Details);
                } else {
                  setProductLotValue(undefined);
                  setProductLot(undefined);
                }
              }

              if (props?.config?.bypassValidation && !value) {
                setProductLotValue(searchText);
                setProductLot({ LotSerial: searchText });
              }
            }}
            suppressDescription={false}
            descriptionField={'Label'}
            isWarehouseProductBinScoped={true}
            params={{ ProductId, Warehouse: WarehouseEntity, Bin: '' }}
            shouldEnterBehaveAsTab={true}
            searchScreenAPIParms={{ Type: 'LOOKUP' }}
            suppressValidation={props?.config?.bypassValidation}
          />
          <NumericField
            label={'Quantity'}
            size={'small'}
            onFocus={onQuantityFocus}
            value={quantity}
            onBlur={setQuantity}
            onChange={onQuantityChange}
            ref={quantityRef}
            onKeyDown={onQuantityKeyDown}
            class={classNames(classes.inlineFieldsSpacing)}
          />
        </div>
        <div className={classes.inlineFields}>
          <MUIButton
            onClick={() => { addLine(); }}
            variant={'contained'}
            disabled={isApplyDisabled}
            className={classNames(classes.button, isApplyDisabled && classes.disabledBtn)}
          >
            <CheckCircleIcon style={{ color: 'green' }} />
            APPLY
          </MUIButton>
          <MUIButton
            onClick={undoChanges}
            variant={'contained'}
            className={classNames(classes.button)}
          >
            <CancelIcon style={{ color: 'red' }} />
            Undo
          </MUIButton>
        </div>
      </div>
      <DynamicDataGrid
        dataGridClassName={classNames({
          [classes.lotLineDataGrid]: !Boolean(isBinTracked),
          [classes.binLotLineDataGrid]: Boolean(isBinTracked),
        })}
        onEditClick={onEditClick}
        columns={generateColumns(isBinTracked)}
        allowHeaderFilting={false}
        allowUpdating={true}
        allowDelete={true}
        actionButtonsVisibility={true}
        onDelete={deleteClick}
        gridProps={{
          dataSource: linesDataSource,
          dataGridRef: gridRef,
          sorting: { mode: 'single' },
          disableDrillDown: false,
          disableEditing: false,
          disableDelete: false,
        }}
      >
      </DynamicDataGrid >
      <Table>
        <TableBody>
          <TableRow className={classes.requiredEnteredBottomRow}>
            <TableCell className={classes.requiredEnteredHeadingCell}>REQUIRED</TableCell>
            <TableCell className={classes.requiredEnteredEmphasizedCell}>{Number(RequiredQuantity || 0).toFixed(NumberOfDecimalPlaces)}</TableCell>
            <TableCell className={classes.requiredEnteredHeadingCell}>ENTERED</TableCell>
            <TableCell className={classes.requiredEnteredDataCell}>{Number(enteredQuantity).toFixed(NumberOfDecimalPlaces)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>;
  };

  const calculateLotsHandler = async (data) => {
    const binLotCriteria = { ...(!isBinTracked && { Bin: '' }), ...data };
    if (binLotCriteria?.Bin == null) {
      binLotCriteria.Bin = '';
    }

    const response = await calculateLotsMutateAsync({ ProductId, Warehouse: WarehouseEntity, BinLotCriteria: { ...binLotCriteria, DocumentType: DocumentType || undefined }, BinLots: lines ?? [] });
    setLines(response?.LotSerial ?? []);
    setAddLotsOpen(false);
  };

  return (
    <>
      {addLotsOpen && <AddLots
        open={addLotsOpen}
        defaultBin={defaultBin}
        calculateLotsHandler={editData ? calculateLineRow : calculateLotsHandler}
        isBinTracked={isBinTracked}
        productId={ProductId}
        warehouseId={WarehouseEntity}
        editData={editData}
        onCancel={() => {
          setEditData(null);
          setAddLotsOpen(false);
        }}
      />}
      <FormViewModal
        open={open}
        loading={false}
        title={title || 'Select Lot Numbers'}
        modalContent={getModalContent()}
        actions={confirmationActions()}
        dialogActionsButtons={true}
        dialogActionsShadow={false}
      />
    </>
  );
};

export default withStyles(styles, { index: 1 })(LotDialog);
